import type { FunnelCounts, FunnelTier } from "@/lib/conceptRail";

interface Props {
  counts: FunnelCounts;
  frontier: FunnelTier | null;
  onTierClick: (tier: FunnelTier) => void;
}

const TIERS: { key: FunnelTier; label: string; color: string }[] = [
  { key: "corpus", label: "Corpus", color: "#89b4fa" },
  { key: "scored", label: "Scored", color: "#a6e3a1" },
  { key: "read", label: "Read", color: "#f9e2af" },
  { key: "evidence", label: "Evidence", color: "#cba6f7" },
];

export function CurateFunnelNest({ counts, frontier, onTierClick }: Props) {
  return (
    <div className="ml-4 mb-2 border-l border-border pl-2.5">
      {TIERS.map(({ key, label, color }) => {
        const value = counts[key];
        const pct = counts.corpus > 0 ? Math.round((value / counts.corpus) * 100) : 0;
        // Floor at 2% — Math.round alone swallows small counts (1 skimmed in a
        // 300-paper corpus rounds to 0% and the segment would never render).
        // Cap at the remaining track so read% + skimmed% never exceeds 100
        // (the floor could otherwise distort the solid segment via flex-shrink).
        const skimmedPct =
          key === "read" && counts.corpus > 0 && counts.skimmed > 0
            ? Math.min(
                Math.max(2, Math.round((counts.skimmed / counts.corpus) * 100)),
                Math.max(0, 100 - pct),
              )
            : 0;
        const isFrontier = frontier === key;
        return (
          <button
            key={key}
            onClick={() => onTierClick(key)}
            aria-current={isFrontier ? "step" : undefined}
            aria-label={`${label} ${value}`}
            title={key === "read" ? `${value} read · ${counts.skimmed} skimmed` : undefined}
            className={`block w-full rounded-md px-1.5 py-1 text-left transition-colors hover:bg-accent/40 ${
              isFrontier ? "bg-emerald-500/[0.07] ring-1 ring-emerald-500/40" : ""
            }`}
          >
            <div className="flex items-baseline justify-between gap-1.5">
              <span className="text-[11px] text-muted-foreground">
                {label}
              </span>
              <span className="font-mono text-[11px] font-semibold" style={{ color }}>
                {value}
              </span>
            </div>
            {key !== "corpus" && (
              <div className="mt-1 flex h-[3px] overflow-hidden rounded-full bg-[#2a2a3e]">
                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
                {skimmedPct > 0 && (
                  <div
                    data-segment="skimmed"
                    className="h-full"
                    style={{ width: `${skimmedPct}%`, background: color, opacity: 0.35 }}
                  />
                )}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}
